import "./Ownership.css";

import {
  Avatar,
  Box,
  Chip,
  Icon,
  Link as LinkButton,
  Tooltip,
  Typography,
} from "@saviynt/design-system";
import cx from "classnames";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import AssignOwnerModal from "./components/AssignOwnerModal/AssignOwnerModal";
import { MAX_VISIBLE_OWNERS } from "../../../../../../constants/policies.constants";

import { sortOwners } from "./utils";
import {
  TriggerSource,
  Ownership as OwnershipType,
} from "@repo/api/schemas/policiesInventory.schema";

interface OwnershipProps {
  ownership?: OwnershipType[];
  assetId: string;
  variant: TriggerSource;
}

export default function Ownership({ ownership = [], assetId }: OwnershipProps) {
  const intl = useIntl();
  const [showAll, setShowAll] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const toggleVisibility = () => setShowAll((prev) => !prev);

  const shouldShowViewAll = useMemo(
    () => ownership.length > MAX_VISIBLE_OWNERS,
    [ownership.length],
  );

  const sortedOwners = sortOwners(ownership);

  return (
    <>
      {isOpen && (
        <AssignOwnerModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          assetId={assetId}
        />
      )}
      <Box className="Ispm-ownership">
        <div className="flex justify-between items-center w-full">
          <Typography kind="h3" desktop="h3-d" mobile="h3-d">
            {intl.formatMessage({
              defaultMessage: "Ownership",
              description: "Section heading for asset ownership information",
              id: "O4x0jH",
            })}
          </Typography>
        </div>
        {sortedOwners.length > 0 ? (
          <div
            className={cx("Ispm-grid-container grid grid-cols-2 gap-4", {
              "Ispm-single-item": sortedOwners.length === 1,
            })}
          >
            {sortedOwners.map(
              ({ name, initials, status, assignmentStatus, source }, index) => {
                const isInActive = status.toLowerCase() === "inactive";
                const isHidden = index >= MAX_VISIBLE_OWNERS && !showAll;
                const assignmentStatusMap = {
                  CONFIRMED: {
                    kind: "CheckCircleFilledSeparateColors" as const,
                    color: "--color-foreground-success-subtle",
                    label: "Confirmed",
                  },
                  PENDING: {
                    kind: "Hourglass" as const,
                    color: "--color-foreground-warning-subtle",
                    label: "Confirmation Pending",
                  },
                };
                const iconConfig = assignmentStatusMap[
                  assignmentStatus as keyof typeof assignmentStatusMap
                ] || { kind: undefined, color: "" };

                return (
                  <div
                    key={`${name}-${initials}`}
                    className={cx("Ispm-grid-item flex items-center gap-3", {
                      hidden: isHidden,
                    })}
                    data-testid={`Ispm-grid-item-${index}`}
                  >
                    <div>
                      <Avatar
                        size="medium"
                        isSelected={isInActive}
                        className={
                          isInActive
                            ? "NHI-avatar-inActive"
                            : "NHI-avatar-active"
                        }
                        classNames={
                          isInActive
                            ? {
                                selectedBorderClassName:
                                  "NHI-avatar-coral-border ",
                              }
                            : undefined
                        }
                      >
                        {initials}
                        {isInActive && (
                          <Icon
                            kind="AlertCriticalFilled"
                            size="medium"
                            className="Ispm-inactive-alert"
                            color="--color-foreground-coral-subtle"
                          />
                        )}
                      </Avatar>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <div className="flex-1 min-w-0">
                          <Typography
                            kind="body1"
                            desktop="body1-d"
                            mobile="body1-d"
                            lineLimit={1}
                            className="truncate-text"
                          >
                            {name}
                          </Typography>
                        </div>
                        {iconConfig.kind && (
                          <Tooltip
                            trigger={
                              <Icon
                                kind={iconConfig.kind}
                                color={iconConfig.color}
                                label={iconConfig.label}
                              />
                            }
                            text={iconConfig.label}
                          />
                        )}
                      </div>
                      {isInActive && (
                        <div className="mt-1">
                          <Typography
                            kind="body3"
                            desktop="body3-d"
                            mobile="body3-d"
                            color="--color-foreground-critical-subtle"
                          >
                            {intl.formatMessage({
                              defaultMessage: "Left the company",
                              description:
                                "Text to indicate the owner is inactive from the company",
                              id: "PaHP3p",
                            })}
                          </Typography>
                        </div>
                      )}
                      <div className="mt-1">
                        <Chip
                          kind="subtleFilled"
                          label={source.join(", ")}
                          size="xxSmall"
                        />
                      </div>
                    </div>
                  </div>
                );
              },
            )}
            {shouldShowViewAll && !showAll && (
              <div className="Ispm-grid-item">
                <Avatar size="medium" className="NHI-bg-primary-subtlest">
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  {`+${sortedOwners.length - MAX_VISIBLE_OWNERS}`}
                </Avatar>
                <Box>
                  <LinkButton
                    text={intl.formatMessage({
                      defaultMessage: "View All",
                      description: "Button text to view all owners",
                      id: "qo6is+",
                    })}
                    kind="button"
                    onClick={toggleVisibility}
                  />
                </Box>
              </div>
            )}
            {showAll && (
              <div className="Ispm-grid-item">
                <Avatar size="medium" className="NHI-bg-primary-subtlest">
                  <Icon kind="VisibilityOff" />
                </Avatar>
                <LinkButton
                  text={intl.formatMessage({
                    defaultMessage: "Hide Extra",
                    description: "Button text to hide extra owners",
                    id: "M6uf2/",
                  })}
                  kind="button"
                  onClick={toggleVisibility}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="Ispm-no-ownership">
            <Typography
              kind="body2"
              desktop="body2-d"
              mobile="body2-d"
              className="Ispm-no-owners-msg"
            >
              {intl.formatMessage({
                defaultMessage: "There are no owners.",
                description: "Text to indicate there are no owners",
                id: "n2qDr1",
              })}
            </Typography>
          </div>
        )}
      </Box>
    </>
  );
}
